import { AnimatePresence } from 'framer-motion';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { RouteManager } from '../../../RouteManager';
import { AppearanceDialog } from '../../appearance/AppearanceDialog';
import { NotificationsDialog } from '../../notifications/NotificationsDialog';
import { PresenceDialog } from '../../presence/PresenceDialog';

import {
  selectedHomeyStore,
  type SelectedHomeyStoreState,
} from '../../../store/SelectedHomeyStore';

import { useIsFullscreen } from '../../../store/web-app-settings/useWebAppSettings';

/** Keeps navigation dialogs mounted when the loading layout is replaced by a page layout. */
export function NavDialogsController() {
  const location = useLocation();
  const homeyMatch = useRouteMatch<{ homeyId: string }>(
    `${import.meta.env.BASE_URL}homeys/:homeyId`,
  );
  const homeyDialogState = selectedHomeyStore.store(selectHomeyDialogState, shallow);
  const { isFullscreen } = useIsFullscreen();

  const isSelectedHomeyRoute =
    homeyMatch != null && homeyMatch.params.homeyId === homeyDialogState.homeyId;
  const isHomeyReady = isSelectedHomeyRoute && homeyDialogState.isReady;

  const presenceUserId = RouteManager.getDialogValue(
    location.state,
    RouteManager.dialogState.userPresenceDialog,
  );
  const isTimelineOpen =
    RouteManager.getDialogValue(location.state, RouteManager.dialogState.timelineDialog) === true;
  const isAppearanceOpen =
    RouteManager.getDialogValue(location.state, RouteManager.dialogState.appearanceDialog) === true;

  return (
    <>
      <AnimatePresence>
        {isHomeyReady && presenceUserId != null && (
          <PresenceDialog userId={presenceUserId} onClose={handlePresenceClose} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isHomeyReady && isFullscreen === false && isTimelineOpen && (
          <NotificationsDialog onClose={handleTimelineClose} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFullscreen === false && isAppearanceOpen && (
          <AppearanceDialog onClose={handleAppearanceClose} />
        )}
      </AnimatePresence>
    </>
  );
}

function selectHomeyDialogState(state: SelectedHomeyStoreState) {
  const isInitialized = state.loading === false && state.error == null && state.api != null;
  const isUpdatingOrRebooting = state.isUpdating === true || state.isRebooting === true;

  return {
    homeyId: state.selectedHomey?.id ?? null,
    isReady: isInitialized && isUpdatingOrRebooting === false,
  };
}

function handlePresenceClose() {
  RouteManager.closeDialog(RouteManager.dialogState.userPresenceDialog);
}

function handleTimelineClose() {
  RouteManager.closeDialog(RouteManager.dialogState.timelineDialog);
}

function handleAppearanceClose() {
  RouteManager.closeDialog(RouteManager.dialogState.appearanceDialog);
}
